import React, { useEffect, useMemo, useState } from 'react';
import { signInWithPopup, signInWithRedirect, User } from 'firebase/auth';
import { AlertTriangle, ClipboardList, Copy, ExternalLink, Loader2, Lock, Plus, Save, Send, Trash2 } from 'lucide-react';
import { auth, googleProvider } from '../firebase';
import {
  getOutlookRecordForRecorder,
  saveOutlookRecordDraftForRecorder,
  submitOutlookRecordForReview,
} from '../services/firebaseService';
import {
  OutlookIssueItem,
  OutlookMeetingFeedback,
  OutlookMeetingRecord,
  OutlookReportGroup,
} from '../types';
import { ToastType } from './Toast';

interface OutlookRecordSubmitProps {
  recordId: string | null;
  user: User | null;
  showToast: (type: ToastType, title: string, message?: string) => void;
}

const textInputClass = 'w-full rounded-lg border-2 border-gray-200 px-3 py-2 text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100';
const numberInputClass = 'w-full rounded-lg border-2 border-gray-200 px-3 py-2 text-right font-black text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100';
const textareaClass = 'w-full min-h-24 rounded-lg border-2 border-gray-200 px-3 py-2 leading-6 text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100';

const makeId = (prefix: string) => `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const parseStatCount = (value: string): number => {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

const getRecorderSubmitUrl = (recordId: string): string =>
  `${window.location.origin}/outlook-records/submit/${encodeURIComponent(recordId)}`;

const isEmbeddedBrowser = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  return /Line\/|FBAN|FBAV|FB_IAB|FB4A|FBIOS|Instagram|Messenger|MicroMessenger/i.test(navigator.userAgent);
};

const isMobileBrowser = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
};

const describeAuthError = (error: unknown): string => {
  const code = (error as { code?: string })?.code;
  if (code === 'auth/popup-closed-by-user') return '登入視窗已關閉，請重新嘗試。';
  if (code === 'auth/popup-blocked') return '登入視窗被瀏覽器阻擋，請改用重新導向登入。';
  if (code === 'auth/unauthorized-domain') return '目前網域尚未被 Firebase Auth 授權，請聯繫管理員。';
  if (code === 'auth/network-request-failed') return '網路連線不穩，請切換網路後再試一次。';
  if (code === 'auth/web-storage-unsupported') return '此瀏覽器阻擋登入所需的儲存功能，請改用 Safari 或 Chrome 開啟。';
  return 'Google 登入失敗，請重新嘗試。若登入後仍回到此畫面，請複製連結後改用 Safari 或 Chrome。';
};

const statusLabel: Record<string, string> = {
  draft: '草稿',
  review: '已送出審核',
  published: '已發布',
};

const updateGroup = (
  groups: OutlookReportGroup[],
  groupId: string,
  patch: Partial<OutlookReportGroup>
): OutlookReportGroup[] => groups.map(group => group.id === groupId ? { ...group, ...patch } : group);

const updateIssue = (
  groups: OutlookReportGroup[],
  groupId: string,
  itemId: string,
  patch: Partial<OutlookIssueItem>
): OutlookReportGroup[] => groups.map(group => group.id === groupId
  ? {
    ...group,
    items: group.items.map(item => item.id === itemId ? { ...item, ...patch } : item),
  }
  : group);

const updateFeedback = (
  feedbacks: OutlookMeetingFeedback[],
  feedbackId: string,
  patch: Partial<OutlookMeetingFeedback>
): OutlookMeetingFeedback[] =>
  feedbacks.map(feedback => feedback.id === feedbackId ? { ...feedback, ...patch } : feedback);

const OutlookRecordSubmit: React.FC<OutlookRecordSubmitProps> = ({ recordId, user, showToast }) => {
  const [record, setRecord] = useState<OutlookMeetingRecord | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [accessError, setAccessError] = useState('');
  const isInEmbeddedBrowser = useMemo(() => isEmbeddedBrowser(), []);
  const isOnMobile = useMemo(() => isMobileBrowser(), []);
  const recorderSubmitUrl = useMemo(() => recordId ? getRecorderSubmitUrl(recordId) : '', [recordId]);

  const sortedGroups = useMemo(
    () => (record?.reportGroups || [])
      .slice()
      .sort((a, b) => a.order - b.order),
    [record?.reportGroups]
  );

  const sortedFeedbacks = useMemo(
    () => (record?.meetingFeedbacks || [])
      .slice()
      .sort((a, b) => a.order - b.order),
    [record?.meetingFeedbacks]
  );

  const totals = useMemo(() => {
    const patientTotal = record?.dayStats.reduce((sum, day) => sum + day.patientCount, 0) || 0;
    const visitTotal = record?.dayStats.reduce((sum, day) => sum + day.visitCount, 0) || 0;
    return { patientTotal, visitTotal };
  }, [record]);

  useEffect(() => {
    if (!recordId || !user) return;

    let mounted = true;
    setIsLoading(true);
    setAccessError('');

    getOutlookRecordForRecorder(recordId)
      .then(data => {
        if (mounted) setRecord(data);
      })
      .catch(error => {
        console.error(error);
        if (mounted) {
          setRecord(null);
          setAccessError('此 Google 帳號未被指派為本梯紀錄者，或連結已失效。');
        }
      })
      .finally(() => {
        if (mounted) setIsLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [recordId, user]);

  const handleLogin = async () => {
    setAccessError('');
    setIsSigningIn(true);
    try {
      if (recorderSubmitUrl) {
        try {
          window.sessionStorage.setItem('tjcmedOutlookRecorderReturnUrl', recorderSubmitUrl);
        } catch (storageError) {
          console.warn('Unable to persist recorder return url before login:', storageError);
        }
      }

      if (isOnMobile) {
        await signInWithRedirect(auth, googleProvider);
        return;
      }
      await signInWithPopup(auth, googleProvider);
    } catch (error) {
      console.error(error);
      if ((error as { code?: string }).code === 'auth/popup-blocked') {
        try {
          await signInWithRedirect(auth, googleProvider);
          return;
        } catch (redirectError) {
          console.error(redirectError);
          setAccessError(describeAuthError(redirectError));
          return;
        }
      }
      setAccessError(describeAuthError(error));
    } finally {
      setIsSigningIn(false);
    }
  };

  const copyCurrentLink = async () => {
    if (!recorderSubmitUrl) return;

    try {
      await navigator.clipboard.writeText(recorderSubmitUrl);
      showToast('success', '連結已複製', '請貼到 Safari 或 Chrome 後再登入。');
    } catch (error) {
      console.error(error);
      setAccessError('無法自動複製連結，請改用右上角選單選擇在瀏覽器開啟。');
    }
  };

  const updateRecord = (patch: Partial<OutlookMeetingRecord>) => {
    setRecord(prev => prev ? { ...prev, ...patch } : prev);
  };

  const updateDayStat = (id: string, patch: Partial<OutlookMeetingRecord['dayStats'][number]>) => {
    updateRecord({
      dayStats: record?.dayStats.map(day => day.id === id ? { ...day, ...patch } : day) || [],
    });
  };

  const addDayStat = () => {
    updateRecord({
      dayStats: [
        ...(record?.dayStats || []),
        {
          id: makeId('day'),
          label: `DAY${(record?.dayStats.length || 0) + 1}`,
          patientCount: 0,
          visitCount: 0,
        },
      ],
    });
  };

  const removeDayStat = (id: string) => {
    updateRecord({
      dayStats: (record?.dayStats || []).filter(day => day.id !== id),
    });
  };

  const updateDepartmentStat = (id: string, patch: Partial<OutlookMeetingRecord['departmentStats'][number]>) => {
    updateRecord({
      departmentStats: record?.departmentStats.map(dept => dept.id === id ? { ...dept, ...patch } : dept) || [],
    });
  };

  const addDepartmentStat = () => {
    updateRecord({
      departmentStats: [
        ...(record?.departmentStats || []),
        {
          id: makeId('dept'),
          name: '',
          visitCount: 0,
        },
      ],
    });
  };

  const removeDepartmentStat = (id: string) => {
    updateRecord({
      departmentStats: (record?.departmentStats || []).filter(dept => dept.id !== id),
    });
  };

  const saveRecord = async (submitForReview: boolean) => {
    if (!recordId || !record) return;

    const hasFeedbackContent = (record.meetingFeedbacks || []).some(feedback =>
      feedback.category.trim() && feedback.speaker.trim() && feedback.summary.trim()
    );
    if (submitForReview && !hasFeedbackContent) {
      showToast('info', '尚未完成', '請先填寫整體回饋後再送出審核。');
      return;
    }
    if (submitForReview && (totals.patientTotal <= 0 || totals.visitTotal <= 0)) {
      showToast('info', '尚未完成', '請先填寫總人數與總診次後再送出審核。');
      return;
    }

    setIsSaving(true);
    try {
      const saved = submitForReview
        ? await submitOutlookRecordForReview(recordId, record)
        : await saveOutlookRecordDraftForRecorder(recordId, record);
      setRecord(saved);
      showToast(
        'success',
        submitForReview ? '已送出審核' : '草稿已儲存',
        saved.title
      );
    } catch (error) {
      console.error(error);
      showToast('error', '儲存失敗', '請確認您使用的是被指派的 Google 帳號。');
    } finally {
      setIsSaving(false);
    }
  };

  if (!recordId) {
    return (
      <div className="container mx-auto px-4">
        <div className="rounded-xl border border-red-100 bg-red-50 p-6 text-red-700">
          紀錄連結不完整，請向管理員索取新的填寫連結。
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="container mx-auto max-w-lg px-4">
        <div className="rounded-2xl border border-gray-200 bg-white p-8 text-center shadow-sm">
          <Lock className="mx-auto mb-4 text-indigo-600" size={42} />
          <h1 className="mb-2 text-2xl font-black text-gray-900">展望會紀錄填寫</h1>
          <p className="mb-6 text-sm font-bold leading-6 text-gray-500">
            請使用被指派的 Google 帳號登入。連結本身不代表權限。
          </p>
          {isInEmbeddedBrowser && (
            <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50 p-4 text-left text-sm font-bold leading-6 text-amber-900">
              <div className="mb-2 flex items-center gap-2 text-amber-800">
                <AlertTriangle size={18} />
                <span>目前可能正在使用 LINE / Facebook 內建瀏覽器</span>
              </div>
              <p>Google 登入可能會回到登入畫面。請點右上角選單，選擇「在瀏覽器開啟」，再使用被指派的 Google 帳號登入。</p>
              <button
                type="button"
                onClick={copyCurrentLink}
                className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-amber-300 bg-white px-4 py-2 text-sm font-black text-amber-900 hover:bg-amber-100"
              >
                <Copy size={16} /> 複製連結
              </button>
            </div>
          )}
          {!isInEmbeddedBrowser && isOnMobile && (
            <div className="mb-5 rounded-xl border border-blue-100 bg-blue-50 p-4 text-left text-sm font-bold leading-6 text-blue-900">
              <p>若登入後又回到此畫面，通常是瀏覽器沒有保留 Google 登入狀態。請複製連結，用 Safari 或 Chrome 直接開啟後再登入。</p>
              <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={copyCurrentLink}
                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-blue-200 bg-white px-4 py-2 text-sm font-black text-blue-900 hover:bg-blue-100"
                >
                  <Copy size={16} /> 複製連結
                </button>
                <a
                  href={recorderSubmitUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-blue-200 bg-white px-4 py-2 text-sm font-black text-blue-900 hover:bg-blue-100"
                >
                  <ExternalLink size={16} /> 新分頁開啟
                </a>
              </div>
            </div>
          )}
          <button
            type="button"
            onClick={handleLogin}
            disabled={isSigningIn}
            className="inline-flex w-full items-center justify-center gap-2 rounded-xl border-2 border-gray-200 bg-white px-5 py-3 font-bold text-gray-700 hover:border-indigo-200 hover:bg-indigo-50 disabled:cursor-wait disabled:opacity-60"
          >
            {isSigningIn && <Loader2 className="animate-spin" size={18} />}
            {isSigningIn ? '正在前往 Google 登入...' : '使用 Google 帳號登入'}
          </button>
          {accessError && <p className="mt-4 text-sm font-bold text-red-600">{accessError}</p>}
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Loader2 className="animate-spin text-indigo-600" size={40} />
      </div>
    );
  }

  if (accessError || !record) {
    return (
      <div className="container mx-auto max-w-2xl px-4">
        <div className="rounded-xl border border-red-100 bg-red-50 p-6 text-red-700">
          <div className="mb-2 flex items-center gap-2 font-black">
            <AlertTriangle size={20} /> 無法開啟紀錄
          </div>
          <p className="text-sm font-bold leading-6">{accessError || '紀錄不存在。'}</p>
        </div>
      </div>
    );
  }

  const isPublished = record.status === 'published';

  return (
    <div className="container mx-auto max-w-6xl space-y-6 px-4">
      <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <div className="mb-2 flex items-center gap-3">
              <ClipboardList className="text-indigo-600" size={28} />
              <h1 className="text-2xl font-black text-gray-900">展望會紀錄填寫</h1>
            </div>
            <p className="font-bold text-gray-700">{record.title}</p>
            <p className="mt-1 text-sm font-bold text-gray-500">
              {record.eventDateRange} / {record.eventLocation}
            </p>
          </div>
          <span className="inline-flex rounded-xl border border-indigo-100 bg-indigo-50 px-4 py-2 text-sm font-black text-indigo-700">
            {statusLabel[record.status] || record.status}
          </span>
        </div>
      </div>

      <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
        <h2 className="mb-4 text-lg font-black text-gray-900">基本資料</h2>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <label>
            <span className="mb-1 block text-sm font-bold text-gray-700">會議日期時間</span>
            <input className={textInputClass} value={record.meetingDateTime} onChange={e => updateRecord({ meetingDateTime: e.target.value })} disabled={isPublished} />
          </label>
          <label>
            <span className="mb-1 block text-sm font-bold text-gray-700">會議地點</span>
            <input className={textInputClass} value={record.meetingLocation} onChange={e => updateRecord({ meetingLocation: e.target.value })} disabled={isPublished} />
          </label>
          <label>
            <span className="mb-1 block text-sm font-bold text-gray-700">紀錄者</span>
            <input className={textInputClass} value={record.recorderName} onChange={e => updateRecord({ recorderName: e.target.value })} disabled={isPublished} />
          </label>
        </div>
      </section>

      <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-center justify-between gap-3">
          <h2 className="text-lg font-black text-gray-900">資訊組統計</h2>
          <div className="text-right text-sm font-black text-gray-700">
            <p>總人數 {totals.patientTotal}</p>
            <p className="text-indigo-700">總診次 {totals.visitTotal}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
          <div className="rounded-xl border border-gray-200">
            <div className="flex items-center justify-between gap-3 border-b bg-gray-50 px-4 py-3">
              <h3 className="font-black text-gray-900">每日人次與診次</h3>
              {!isPublished && (
                <button
                  type="button"
                  onClick={addDayStat}
                  className="inline-flex items-center gap-1 rounded-lg border border-indigo-200 bg-white px-3 py-1.5 text-sm font-bold text-indigo-700 hover:bg-indigo-50"
                >
                  <Plus size={15} /> 新增場次
                </button>
              )}
            </div>
            <div className="space-y-3 p-4">
              {(record.dayStats || []).map(day => (
                <div
                  key={day.id}
                  className={`grid grid-cols-1 gap-3 rounded-xl border border-gray-200 bg-white p-3 md:items-end ${
                    isPublished ? 'md:grid-cols-[1fr_120px_120px]' : 'md:grid-cols-[1fr_120px_120px_auto]'
                  }`}
                >
                  <label>
                    <span className="mb-1 block text-xs font-bold text-gray-500">場次</span>
                    <input className={textInputClass} value={day.label} onChange={e => updateDayStat(day.id, { label: e.target.value })} disabled={isPublished} />
                  </label>
                  <label>
                    <span className="mb-1 block text-xs font-bold text-gray-500">人數</span>
                    <input
                      type="number"
                      min={0}
                      className={numberInputClass}
                      value={day.patientCount}
                      onChange={e => updateDayStat(day.id, { patientCount: parseStatCount(e.target.value) })}
                      disabled={isPublished}
                    />
                  </label>
                  <label>
                    <span className="mb-1 block text-xs font-bold text-gray-500">診次</span>
                    <input
                      type="number"
                      min={0}
                      className={numberInputClass}
                      value={day.visitCount}
                      onChange={e => updateDayStat(day.id, { visitCount: parseStatCount(e.target.value) })}
                      disabled={isPublished}
                    />
                  </label>
                  {!isPublished && (
                    <button
                      type="button"
                      onClick={() => removeDayStat(day.id)}
                      disabled={(record.dayStats || []).length <= 1}
                      className="inline-flex h-11 items-center justify-center gap-1 rounded-lg border border-red-100 px-3 text-sm font-bold text-red-600 hover:bg-red-50 disabled:opacity-40"
                    >
                      <Trash2 size={16} /> 刪除
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-gray-200">
            <div className="flex items-center justify-between gap-3 border-b bg-gray-50 px-4 py-3">
              <h3 className="font-black text-gray-900">各科診次</h3>
              {!isPublished && (
                <button
                  type="button"
                  onClick={addDepartmentStat}
                  className="inline-flex items-center gap-1 rounded-lg border border-indigo-200 bg-white px-3 py-1.5 text-sm font-bold text-indigo-700 hover:bg-indigo-50"
                >
                  <Plus size={15} /> 新增科別
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 gap-3 p-4 md:grid-cols-2">
              {(record.departmentStats || []).map(dept => (
                <div
                  key={dept.id}
                  className={`grid grid-cols-1 gap-2 rounded-xl border border-gray-200 bg-white p-3 sm:items-end ${
                    isPublished ? 'sm:grid-cols-[1fr_96px]' : 'sm:grid-cols-[1fr_96px_auto]'
                  }`}
                >
                  <label>
                    <span className="mb-1 block text-xs font-bold text-gray-500">科別</span>
                    <input className={textInputClass} value={dept.name} onChange={e => updateDepartmentStat(dept.id, { name: e.target.value })} disabled={isPublished} />
                  </label>
                  <label>
                    <span className="mb-1 block text-xs font-bold text-gray-500">診次</span>
                    <input
                      type="number"
                      min={0}
                      className={numberInputClass}
                      value={dept.visitCount}
                      onChange={e => updateDepartmentStat(dept.id, { visitCount: parseStatCount(e.target.value) })}
                      disabled={isPublished}
                    />
                  </label>
                  {!isPublished && (
                    <button
                      type="button"
                      onClick={() => removeDepartmentStat(dept.id)}
                      disabled={(record.departmentStats || []).length <= 1}
                      className="inline-flex h-11 w-full items-center justify-center rounded-lg border border-red-100 text-red-600 hover:bg-red-50 disabled:opacity-40 sm:w-11"
                      title="刪除"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-2">
          <label>
            <span className="mb-1 block text-sm font-bold text-gray-700">男女比</span>
            <input className={textInputClass} value={record.genderRatio} onChange={e => updateRecord({ genderRatio: e.target.value })} disabled={isPublished} />
          </label>
          <label>
            <span className="mb-1 block text-sm font-bold text-gray-700">年齡摘要</span>
            <input className={textInputClass} value={record.ageRange} onChange={e => updateRecord({ ageRange: e.target.value })} disabled={isPublished} />
          </label>
          <label>
            <span className="mb-1 block text-sm font-bold text-gray-700">信仰分布</span>
            <textarea className={textareaClass} value={record.faithDistribution} onChange={e => updateRecord({ faithDistribution: e.target.value })} disabled={isPublished} />
          </label>
          <label>
            <span className="mb-1 block text-sm font-bold text-gray-700">如何得知義診</span>
            <textarea className={textareaClass} value={record.referralSourceDistribution} onChange={e => updateRecord({ referralSourceDistribution: e.target.value })} disabled={isPublished} />
          </label>
        </div>
      </section>

      <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
        <h2 className="mb-4 text-lg font-black text-gray-900">各組回報</h2>
        <div className="space-y-5">
          {sortedGroups.map(group => (
            <div key={group.id} className="rounded-xl border border-gray-200 p-4">
              <h3 className="mb-3 text-lg font-black text-gray-900">{group.name}</h3>
              <div className="rounded-xl border border-gray-200 bg-white p-3">
                <label className="block">
                  <span className="mb-1 block text-xs font-bold text-gray-500">本組執行亮點與可延續做法</span>
                  <textarea
                    className={textareaClass}
                    value={group.currentSummary}
                    onChange={e => updateRecord({ reportGroups: updateGroup(record.reportGroups, group.id, { currentSummary: e.target.value }) })}
                    disabled={isPublished}
                    placeholder="例：流程順暢、分工清楚、民眾等待時間縮短，可作為下次流程參考。"
                  />
                </label>
              </div>

              <div className="mt-4 space-y-3">
                {group.items.map(item => (
                  <div key={item.id} className="rounded-xl border border-gray-200 bg-white p-3">
                    <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
                      <label>
                        <span className="mb-1 block text-xs font-bold text-gray-500">問題</span>
                        <textarea className={textareaClass} value={item.issue} onChange={e => updateRecord({ reportGroups: updateIssue(record.reportGroups, group.id, item.id, { issue: e.target.value }) })} disabled={isPublished} />
                      </label>
                      <label>
                        <span className="mb-1 block text-xs font-bold text-gray-500">下次建議</span>
                        <textarea className={textareaClass} value={item.improvementMethod} onChange={e => updateRecord({ reportGroups: updateIssue(record.reportGroups, group.id, item.id, { improvementMethod: e.target.value }) })} disabled={isPublished} />
                      </label>
                    </div>
                    <label className="mt-3 block max-w-md">
                      <span className="mb-1 block text-xs font-bold text-gray-500">追蹤負責人</span>
                      <input className={textInputClass} value={item.owner} onChange={e => updateRecord({ reportGroups: updateIssue(record.reportGroups, group.id, item.id, { owner: e.target.value }) })} disabled={isPublished} />
                    </label>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
        <h2 className="mb-4 text-lg font-black text-gray-900">整體回饋</h2>
        <div className="space-y-4">
          {sortedFeedbacks.map(feedback => (
            <div key={feedback.id} className="rounded-xl border border-gray-200 bg-white p-4">
              <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                <label>
                  <span className="mb-1 block text-xs font-bold text-gray-500">類別</span>
                  <input className={textInputClass} value={feedback.category} onChange={e => updateRecord({ meetingFeedbacks: updateFeedback(record.meetingFeedbacks || [], feedback.id, { category: e.target.value }) })} disabled={isPublished} />
                </label>
                <label>
                  <span className="mb-1 block text-xs font-bold text-gray-500">發言單位/人員</span>
                  <input className={textInputClass} value={feedback.speaker} onChange={e => updateRecord({ meetingFeedbacks: updateFeedback(record.meetingFeedbacks || [], feedback.id, { speaker: e.target.value }) })} disabled={isPublished} />
                </label>
                <label>
                  <span className="mb-1 block text-xs font-bold text-gray-500">重點摘要</span>
                  <textarea className={textareaClass} value={feedback.summary} onChange={e => updateRecord({ meetingFeedbacks: updateFeedback(record.meetingFeedbacks || [], feedback.id, { summary: e.target.value }) })} disabled={isPublished} />
                </label>
              </div>
            </div>
          ))}
        </div>
      </section>

      {!isPublished && (
        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <button
              type="button"
              onClick={() => saveRecord(false)}
              disabled={isSaving}
              className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-gray-200 px-5 py-3 font-bold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <Save size={18} /> 儲存草稿
            </button>
            <button
              type="button"
              onClick={() => saveRecord(true)}
              disabled={isSaving}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-amber-500 px-5 py-3 font-bold text-white hover:bg-amber-600 disabled:opacity-50"
            >
              <Send size={18} /> 送出審核
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OutlookRecordSubmit;
